/**
 * Session queries and mutations: sign-in, first-run setup, sign-out,
 * password change, and permission checks.
 *
 * @module
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, setCsrfToken, unwrap, type Permission, type SessionInfo } from "../api/client";
import { reset as resetTransfers } from "../import/transfers";

export const SESSION_KEY = ["session"] as const;

/** Current session, or null when signed out. */
export function useSession() {
  return useQuery({
    queryKey: SESSION_KEY,
    queryFn: async (): Promise<SessionInfo | null> => {
      try {
        const s = await unwrap(api.GET("/api/auth/me"));
        setCsrfToken(s.csrf_token);
        return s;
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) {
          setCsrfToken(null);
          return null;
        }
        throw e;
      }
    },
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { username: string; password: string }) =>
      unwrap(api.POST("/api/auth/login", { body })),
    onSuccess: (s) => {
      setCsrfToken(s.csrf_token);
      qc.setQueryData(SESSION_KEY, s);
    },
  });
}

/** Whether this desktop app still needs its first administrator. */
export function useSetupStatus(enabled: boolean) {
  return useQuery({
    queryKey: ["setup"],
    queryFn: () => unwrap(api.GET("/api/setup")),
    enabled,
  });
}

export function useSetup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { username: string; display_name: string; password: string }) =>
      unwrap(api.POST("/api/setup", { body })),
    onSuccess: (s) => {
      setCsrfToken(s.csrf_token);
      qc.setQueryData(SESSION_KEY, s);
      qc.setQueryData(["setup"], { needed: false });
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(api.POST("/api/auth/logout")),
    onSettled: () => {
      resetTransfers();
      setCsrfToken(null);
      // Not qc.clear(): it drops the session query its observer still watches, so the null never renders.
      qc.setQueryData(SESSION_KEY, null);
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== SESSION_KEY[0] });
    },
  });
}

export function useChangePassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { current_password: string; new_password: string }) =>
      unwrap(api.POST("/api/auth/change-password", { body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: SESSION_KEY }),
  });
}

export function hasPermission(s: SessionInfo, p: Permission): boolean {
  return s.permissions.includes(p);
}
